import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { DoesNotExistAspectConfig, ExistsAspectConfig } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import {
  type ZipCityAspect,
  ZipCityAspectSchema,
} from '@chancenwerk/shared/master-data-management/aspects/ZipCityAspect.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/components/chancen-component-zip-city-picker.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { MarktAPI } from '@chancenwerk/markt/api/MarktAPI.js';
import type { MarktAkquise } from '@chancenwerk/markt/entities/MarktAkquise.js';
import type { MarktVeranstaltungsAkquise } from '@chancenwerk/markt/entities/MarktVeranstaltungsAkquise.js';
import { PartnerAPI } from '@chancenwerk/shared/master-data-management/partner/api/PartnerAPI.js';
import {
  getPartnerClassString,
  PartnerClassMap,
} from '@chancenwerk/shared/master-data-management/partner/entities/KooperationsPartner.js';
import { PARTNER_BLACKLIST, Partner } from '@chancenwerk/shared/master-data-management/partner/entities/Partner.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { ChancenPersonalConfigAPI } from '@chancenwerk/shared/user-configs/api/ChancenPersonalConfigAPI.js';
import { ListUIConfig } from '@chancenwerk/shared/user-configs/entities/ListUIConfig.js';
import { newOperationBatch } from '@chancenwerk/shared/utils/batch.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import type { DateTime } from 'luxon';
import '../components/markt-component-status.js';

const batchMarktAkquise = newOperationBatch(
  250,
  (ids: string[]) => {
    return MarktAPI.getMarktAkquise(ids)({ _id: 1, status: 1, partnerID: 1, contacts: { status: 1 } });
  },
  res => res.partnerID ?? '',
);

const batchMarktVerAkquise = newOperationBatch(
  250,
  (ids: string[]) => {
    return MarktAPI.getMarktVeranstaltungsAkquise(ids)({ _id: 1, entries: { contacts: { status: 1 } }, partnerID: 1 });
  },
  res => res.partnerID ?? '',
);

const filterSelector: BaseQLSelectionSet<AdornisFilter | ZipCityAspect> = {
  ...AdornisFilter.allFields,
  aspects: { ...AbstractAspect.allFields, value: 1, zipCity: { zip: 1, city: 1 } },
};

/**
 * @customElement markt-page-overview
 */
@customElement('markt-page-overview')
export class MarktPageWorkshops extends CrPage {
  @state() private readonly _config = new RXController(
    this,
    ChancenPersonalConfigAPI.getByKeyAndCategory(
      ListUIConfig<typeof Partner>,
      'list-markt',
      'default',
    )({
      key: 1,
      userID: 1,
      category: 1,
      view: {
        filter: filterSelector,
        mode: 1,
        configuredColumns: { customID: 1, fieldName: 1, columnWidth: 1 },
        selectedItems: 1,
        multiSelection: 1,
      },
    }),
  );

  @state() _view = new View({ filter: new AdornisFilter({ aspects: [] }) });

  constructor() {
    super();

    this.label = 'Übersicht';
    this.description = 'Übersicht über das Hochschulmarketing';
    this.permission = ChancenPermission.markt.LOGIN_MARKT;
  }

  override header() {
    return html``;
  }

  override content() {
    return html`${this._overview()}`;
  }

  private _overview() {
    const options = buildChancenDataViewOptions<typeof Partner>(
      {
        class: Partner,
        operation: PartnerAPI.subscribeByFilter,
        itemSpacing: this.spacing.md,
        defaultFieldMapping: { DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy') },
        view: this._config.value?.view ?? this._view,
        onViewChanged: async view => {
          if (!this._config.value) return;
          this._config.value.view = view;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
        isEntryClickable: true,
        isColumnResizable: true,
        isColumnSelectable: true,
        isColumnResortable: true,
        fieldSelectionOptions: {
          hideFieldsWithoutTranslation: true,
          shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({
            blacklist: { ...PARTNER_BLACKLIST, parent: PARTNER_BLACKLIST, _class: undefined },
          }),
        },
        onlyShowLastTranslation: true,
        customColumns: [
          {
            fieldName: '_class',
            getFieldMapping: entity => getPartnerClassString(entity._class),
            filterAspect: [
              {
                ...ListAspectSchema(
                  Object.values(PartnerClassMap).sort((a, b) => {
                    const nameA = getPartnerClassString(a) ?? '';
                    const nameB = getPartnerClassString(b) ?? '';
                    return nameA.localeCompare(nameB);
                  }),
                  'Art',
                ),
              },
              DoesNotExistAspectConfig,
              ExistsAspectConfig,
            ],
          },
          { fieldName: 'address.zipCity', filterAspect: ZipCityAspectSchema },
          {
            customID: 'status-fachbereich-akquise',
            columnName: 'Status Fachbereich-Akquise',
            getFieldMapping: entityInstance => {
              return until(
                (async () => {
                  const res = (await batchMarktAkquise.get(entityInstance._id)) as MarktAkquise;

                  return html`<markt-component-status
                    .status=${res?.getLatestStatus() ?? 'Leer'}
                    @click=${e => {
                      e.preventDefault();
                      e.stopPropagation();
                      goTo('/markt-akquise/' + entityInstance._id);
                    }}
                  >
                  </markt-component-status>`;
                })(),
              );
            },
          },
          {
            customID: 'status-veranstaltungs-akquise',
            columnName: 'Status Veranstaltungs-Akquise',
            getFieldMapping: entityInstance => {
              return until(
                (async () => {
                  const res = (await batchMarktVerAkquise.get(entityInstance._id)) as MarktVeranstaltungsAkquise;
                  const hasTodo = res?.hasTodo();
                  const isEmpty = res?.entries?.length === 0;

                  return html`<markt-component-status
                    .status=${hasTodo ? 'TODO' : isEmpty ? 'Leer' : 'OK'}
                    @click=${e => {
                      e.preventDefault();
                      e.stopPropagation();
                      goTo('/markt-veranstaltung-akquise/' + entityInstance._id);
                    }}
                  >
                  </markt-component-status>`;
                })(),
              );
            },
          },
        ],
      } as DataViewOptions<typeof Partner>,
      {
        fieldAspectGeneration: {
          hideFieldsWithoutTranslation: true,
          blacklist: { ...PARTNER_BLACKLIST, _class: undefined },
        },
        onFilterChanged: async filter => {
          if (!this._config.value?.view) return;
          this._config.value.view.filter = filter;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
      },
    );

    return html`
      <cr-card padding="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })} margin="sm">
        ${FilterEditor({ noCollapse: true, options: options.filterOptions })}
      </cr-card>
      <cr-flex ${css({ overflow: 'auto' })}>
        <cr-data-view
          ${css({ marginTop: this.spacing.sm })}
          .options=${options.dataViewOptions}
          @entry-clicked=${(e: CustomEvent<{ entity: { _id: string } }>) => {
            window.open(`${projectConfig.get('RAUM_URL')}edit/partner/${e.detail.entity._id}`, '_blank');
          }}
        >
        </cr-data-view>
      </cr-flex>
    `;
  }
}
